// src/hooks/useFixtureAnalytics.js
import { useState, useEffect } from 'react';
import { getFixtureAnalytics } from '../lib/analyticsUtils';

/**
 * Custom hook to fetch analytics data for a fixture
 * @param {object} fixture - Fixture object
 * @param {boolean} enabled - Whether to fetch analytics (default: true)
 * @returns {object} { analytics, loading, error }
 */
export default function useFixtureAnalytics(fixture, enabled = true) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Don't fetch if disabled or no fixture
    if (!enabled || !fixture) {
      setAnalytics(null);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchAnalytics = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await getFixtureAnalytics(fixture);
        
        if (isMounted) {
          setAnalytics(data);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to fetch analytics');
          setLoading(false);
        }
      }
    };

    fetchAnalytics();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [fixture?.id, enabled]); // Re-fetch if fixture ID changes

  return { analytics, loading, error };
}
